import { RootState } from "..";

import { breakoutGroupSelectors, classUserSelectors, createAppSelector } from "./base";
import { selectCurrentBreakoutGroupId, selectIsInBreakout } from "./breakouts";
import { selectActiveClassSession, selectIsClassSessionVirtual } from "./class-session";
import { selectCurrentClassUser } from "./class-user";
import { selectCurrentSection } from "./current";
import {
  selectIsRecordingAgent,
  selectUserIdsWhoAreVisibleInClass,
  selectUserIdsWhoCanParticipateInSection,
} from "./permissions";

export const selectClassUsersInVirtualClass = createAppSelector(
  [classUserSelectors.filterForCurrentSection, selectIsClassSessionVirtual],
  (classUsers, isClassSessionVirtual) =>
    isClassSessionVirtual ? classUsers.filter((cu) => cu.is_in_class_session) : [],
);

export const selectUserIdsInVirtualClass = createAppSelector(
  [selectClassUsersInVirtualClass],
  (classUsers) => classUsers.map((classUser) => classUser.user_id),
);

export const selectVirtualClassAvService = createAppSelector(
  [selectCurrentSection],
  (currentSection) => currentSection?.av_service || null,
);

export const selectVirtualClassLicodeServerUrl = createAppSelector(
  [selectActiveClassSession],
  (currentClassSession) => currentClassSession?.licode_server_url || null,
);

export const selectVirtualClassOpentokSessionId = createAppSelector(
  [selectActiveClassSession],
  (currentClassSession) => currentClassSession?.opentok_session_id || null,
);
// If the user is in a breakout, return the screensharing user for that group.
// Otherwise, return the main class session screensharing user.
export const selectScreensharingClassUserId = createAppSelector(
  [
    selectActiveClassSession,
    selectCurrentBreakoutGroupId,
    breakoutGroupSelectors.selectAll,
    selectIsInBreakout,
  ],
  (classSession, breakoutGroupId, breakoutGroups, isInBreakout) => {
    if (breakoutGroupId && isInBreakout) {
      const breakoutGroup = breakoutGroups.find((bg) => bg.id === breakoutGroupId);
      return breakoutGroup?.screensharing_class_user_id || null;
    }
    return classSession?.screensharing_class_user_id || null;
  },
);

export const selectIsScreensharingOn = createAppSelector(
  [selectScreensharingClassUserId],
  (screensharingClassUserId) => !!screensharingClassUserId,
);

export const selectCanStudentsScreenshare = createAppSelector(
  [selectActiveClassSession],
  (classSession) => classSession?.can_students_screenshare || false,
);

export const selectViewingScreenshare = createAppSelector(
  [
    selectIsRecordingAgent,
    selectIsScreensharingOn,
    (state: RootState) => state.view.viewingScreenshare,
  ],
  (isRecordingAgent, isScreensharingOn, viewingScreenshare) =>
    (isRecordingAgent && isScreensharingOn) || viewingScreenshare,
);

export const selectCountStudentsInVirtualClass = createAppSelector(
  [selectUserIdsWhoCanParticipateInSection, selectUserIdsInVirtualClass],
  (studentUserIds, userIdsInVirtualClass) => {
    return studentUserIds.filter((userId) => userIdsInVirtualClass.includes(userId)).length;
  },
);

export const selectClassUsersWithIsVisibleInClassCapability = createAppSelector(
  [selectClassUsersInVirtualClass, selectUserIdsWhoAreVisibleInClass],
  (classUsersInVirtualClass, visibleUserIds) =>
    classUsersInVirtualClass.filter((classUser) => visibleUserIds.includes(classUser.user_id)),
);

export const selectCurrentUserHasVisibleInClassCapability = createAppSelector(
  [selectCurrentClassUser, selectUserIdsWhoAreVisibleInClass],
  (currentClassUser, visibleUserIds) =>
    !!currentClassUser && visibleUserIds.includes(currentClassUser.user_id),
);

export const selectClassUsersInMainRoom = createAppSelector(
  [selectClassUsersWithIsVisibleInClassCapability],
  (classUsers) => classUsers.filter((classUser) => !classUser.breakout_group_id),
);
